import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { publicApi, ApiError } from '../../lib/api'
import { ThemeProvider } from '../../lib/theme'
import { NoIndex } from '../../components/NoIndex'

type LoadState =
  | { status: 'loading' }
  | { status: 'not_found' }
  | { status: 'error' }
  | {
      status: 'ok'
      data: Awaited<ReturnType<typeof publicApi.catalog>>
    }

function fmtEuro(cents: number) {
  return `${(cents / 100).toFixed(2).replace('.', ',')} €`
}

function seasonLabel(iso: string | null) {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const m = d.getMonth()
  const y = d.getFullYear()
  if (m >= 2 && m <= 4) return `au printemps ${y}`
  if (m >= 5 && m <= 7) return `à l'été ${y}`
  if (m >= 8 && m <= 10) return `à l'automne ${y}`
  return `à l'hiver ${y}`
}

function daysUntil(iso: string | null) {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  d.setHours(0, 0, 0, 0)
  return Math.round((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
}

export default function RegistryPage() {
  const { slug } = useParams<{ slug: string }>()
  const [state, setState] = useState<LoadState>({ status: 'loading' })

  useEffect(() => {
    if (!slug) return
    let cancelled = false
    setState({ status: 'loading' })
    publicApi
      .catalog(slug)
      .then((data) => {
        if (cancelled) return
        setState({ status: 'ok', data })
      })
      .catch((e) => {
        if (cancelled) return
        if (e instanceof ApiError && e.status === 404) setState({ status: 'not_found' })
        else setState({ status: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  if (state.status === 'loading') {
    return (
      <main className="page guest-shell">
        <NoIndex />
        <div className="guest-boot">
          <div className="guest-boot-title">Liste de naissance</div>
          <div className="guest-boot-sub">Chargement…</div>
        </div>
      </main>
    )
  }

  if (state.status === 'not_found') {
    return (
      <main className="page guest-shell">
        <NoIndex />
        <div className="guest-boot">
          <div className="guest-boot-title">Introuvable</div>
          <p className="guest-boot-sub">Cette liste n’existe pas (ou n’est pas publiée).</p>
        </div>
      </main>
    )
  }

  if (state.status === 'error') {
    return (
      <main className="page guest-shell">
        <NoIndex />
        <div className="guest-boot">
          <div className="guest-boot-title">Oups…</div>
          <p className="guest-boot-sub">Impossible de charger la liste pour le moment.</p>
          <button className="btn btn-primary" onClick={() => setState({ status: 'loading' })}>
            Réessayer
          </button>
        </div>
      </main>
    )
  }

  const data = state.data
  const registry = data.registry

  return (
    <ThemeProvider theme={registry.theme} accentColor={registry.accentColor}>
      <main className="page guest-shell">
        <NoIndex />

        <div className="guest-catalog">
          <header className="guest-header">
            {registry.coverImageUrl ? (
              <div className="guest-cover" aria-hidden="true">
                <img src={registry.coverImageUrl} alt="" />
              </div>
            ) : null}

            <p className="guest-eyebrow">Liste de naissance</p>
            <h1 className="guest-h1">{registry.title}</h1>

            <Dateline dueDate={registry.dueDate} />

            {registry.welcomeMessage ? (
              <p className="guest-welcome">{registry.welcomeMessage}</p>
            ) : null}

            <div className="guest-ornament" aria-hidden="true">
              <span className="guest-dot" />
              <span className="guest-line" />
              <span className="guest-dot" />
            </div>
          </header>

          {data.items.length === 0 ? (
            <div className="guest-empty">
              <p>La liste arrive bientôt.</p>
              <p className="muted">Revenez dans quelques jours.</p>
            </div>
          ) : (
            <div className="guest-grid">
              {data.items.map((it) => (
                <ItemCard key={it.id} slug={slug!} item={it} />
              ))}
            </div>
          )}

          <footer className="guest-footer">
            <div className="guest-ornament" aria-hidden="true">
              <span className="guest-dot" />
              <span className="guest-line" />
              <span className="guest-dot" />
            </div>
            <p className="muted guest-footer-note">
              Propulsé par <strong>Liste de Naissance</strong> ·{' '}
              <Link to="/privacy">Confidentialité</Link> · <Link to="/terms">Conditions</Link>
            </p>
          </footer>
        </div>
      </main>
    </ThemeProvider>
  )
}

function Dateline({ dueDate }: { dueDate: string | null }) {
  const season = useMemo(() => seasonLabel(dueDate), [dueDate])
  const days = useMemo(() => daysUntil(dueDate), [dueDate])
  const showCountdown = typeof days === 'number' && days >= 0 && days <= 365

  if (!season && !showCountdown) return null

  return (
    <div className="guest-dateline" aria-label="Date prévue">
      <span className="guest-dateline-text">
        {season ? <span>à naître {season}</span> : null}
        {season && showCountdown ? <span className="guest-dateline-sep">·</span> : null}
        {showCountdown ? (
          <span className="guest-dateline-countdown">
            {days === 0 ? "aujourd'hui" : `J-${days}`}
          </span>
        ) : null}
      </span>
    </div>
  )
}

function ItemCard({
  slug,
  item,
}: {
  slug: string
  item: {
    id: string
    title: string
    description: string | null
    priceCents: number
    imageUrl: string | null
    externalUrl: string | null
    outUrl: string
    offered: boolean
    alreadyGivenCents: number
    remainingCents: number
    complete: boolean
  }
}) {
  const pct =
    item.priceCents > 0
      ? Math.min(100, Math.round((item.alreadyGivenCents / item.priceCents) * 100))
      : 0
  // An item the owner marked as offered is locked for guests just like a fully
  // funded one: no contributions accepted, shown as already taken.
  const locked = item.complete || item.offered

  return (
    <article className={`guest-card ${locked ? 'is-complete' : ''}`}>
      <div className="guest-card-img">
        {locked ? (
          <>
            {item.imageUrl ? <img src={item.imageUrl} alt="" loading="lazy" /> : <Placeholder />}
            <div className="guest-ribbon">
              <span>Déjà offert</span>
            </div>
          </>
        ) : (
          <Link
            className="guest-card-img-link"
            to={`/r/${slug}/contribute/${item.id}`}
            aria-label={`Participer – ${item.title}`}
          >
            {item.imageUrl ? <img src={item.imageUrl} alt="" loading="lazy" /> : <Placeholder />}
          </Link>
        )}
      </div>

      <div className="guest-card-body">
        <h2 className="guest-card-title">{item.title}</h2>
        {item.description ? <p className="guest-card-desc">{item.description}</p> : null}

        <div className="guest-card-price-row">
          <span className="guest-card-price">{fmtEuro(item.priceCents)}</span>
          {!locked ? (
            <span className="guest-card-remaining">
              reste <strong>{fmtEuro(item.remainingCents)}</strong>
            </span>
          ) : null}
        </div>

        <div className="guest-prog" aria-hidden="true">
          <div className="guest-prog-fill" style={{ width: `${pct}%` }} />
        </div>
        <p className="guest-card-progress-label">
          {locked ? (
            <span>Déjà offert</span>
          ) : item.alreadyGivenCents > 0 ? (
            <span>{fmtEuro(item.alreadyGivenCents)} déjà offerts</span>
          ) : (
            <span className="muted">À offrir</span>
          )}
        </p>

        <div className="guest-card-actions">
          {locked ? (
            <span className="muted">Complet ✓</span>
          ) : (
            <Link className="btn btn-primary btn-sm" to={`/r/${slug}/contribute/${item.id}`}>
              Participer
            </Link>
          )}

          {item.externalUrl ? (
            <a
              className="guest-card-link"
              href={item.outUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              voir le produit ↗
            </a>
          ) : null}
        </div>
      </div>
    </article>
  )
}

function Placeholder() {
  return (
    <div className="guest-ph" aria-hidden="true">
      <svg viewBox="0 0 200 200" width="84" height="84">
        <rect width="200" height="200" rx="18" fill="currentColor" opacity="0.08" />
        <path d="M40 110 Q 100 60 160 110 Q 100 160 40 110 Z" fill="currentColor" opacity="0.12" />
      </svg>
    </div>
  )
}
